import { useCallback, useContext, useEffect, useMemo } from "react";
import { AxiosError } from "axios";
import { get, isEqual } from "lodash";
import { PostHog } from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { useQuery, useQueryClient } from "react-query";

import { AppContext } from "contexts";
import {
  makeSimpleGetRequest,
  makeSimplePatchRequest,
  makeSimplePostRequest,
} from "lib/axios";
import { skipRetriesOn404 } from "lib/react-query";
import { DEFAULT_ERROR_MESSAGE, ORDERBOOK_URL } from "services/constants";
import { EXCHANGE_RUNTIME_ERROR } from "services/postHog/constants";
import { getUseProxiesQueryKey } from "services/proxies";
import { processErrors } from "services/utils";

import { EditListing, ListingWithIncludes, ListPricePayload } from "./types";
import websocketManager from "./webSockets";

const EDIT_LISTING_URL = `${ORDERBOOK_URL}/EditListing`;

export const getBuyNowPriceErrorMessage = (
  error: Error | AxiosError,
): string => {
  const { formErrors, fieldErrors } = processErrors(error.message, [
    "buy_now_price",
  ]);
  const buyNowPriceError = get(fieldErrors, "buy_now_price");
  return buyNowPriceError
    ? buyNowPriceError
    : !formErrors || formErrors.length > 0
    ? DEFAULT_ERROR_MESSAGE
    : "";
};

export type UseListingsPayload = ListingWithIncludes;
export const getUseListingsQueryKey = (listingId: number) => [
  "listing",
  listingId,
];
export const useListings = (listingId: number) => {
  const listingQueryKey = useMemo(() => getUseListingsQueryKey(listingId), [
    listingId,
  ]);
  const queryClient = useQueryClient();
  const appContext = useContext(AppContext);
  const user = useMemo(() => appContext.user, [appContext.user]);
  const posthog = usePostHog();

  const listingQueries = useQuery<UseListingsPayload, { message: string }>(
    listingQueryKey,
    () =>
      makeSimpleGetRequest(
        `${ORDERBOOK_URL}/listings/${listingId}/?include[]=asset.*&include[]=valuation_description.*&include[]=offers.*`,
      ),
    {
      enabled: !!listingId,
      staleTime: Infinity,
      retry: skipRetriesOn404,
    },
  );

  const { data, refetch } = listingQueries;

  const handleMessage = useCallback(
    (event: MessageEvent<string>) => {
      try {
        const parsedMessage: ListingWithIncludes = JSON.parse(event.data).data;
        queryClient.setQueryData(
          ["listing", listingId],
          (oldData: UseListingsPayload | undefined) => {
            if (!oldData) {
              // Don't set partial data if we don't have the full data already
              return oldData;
            }
            if (
              !!user &&
              !isEqual(oldData.offers, parsedMessage.offers) &&
              !oldData.is_owner
            ) {
              queryClient.refetchQueries(getUseProxiesQueryKey(listingId));
            }

            // Don't update user dependent properties, websocket messages are serialized for anonymous user
            delete parsedMessage.earnings_file;
            delete parsedMessage.is_in_feature_queue;
            delete parsedMessage.is_owner;
            delete parsedMessage.marketplace_median;
            delete parsedMessage.marketplace_median_multiplier;
            delete parsedMessage.marketplace_median_source;
            delete parsedMessage.title;
            return { ...oldData, ...parsedMessage };
          },
        );
      } catch (e) {
        posthog?.capture(EXCHANGE_RUNTIME_ERROR, {
          errorMessage: "WebSocket onmessage error",
          trace: e.toString(),
        });
      }
    },
    [user, listingId, queryClient, posthog],
  );

  const isListingDataCurrent = useCallback(
    (websocketDataPublishedAt: string) => {
      const isListingDataCurrent =
        data?.last_websocket_message_published_at === websocketDataPublishedAt;
      return {
        isCurrent: isListingDataCurrent,
        lastWebsocketMessagePublishedAt:
          data?.last_websocket_message_published_at,
      };
    },
    [data?.last_websocket_message_published_at],
  );

  useEffect(() => {
    if (!listingId) {
      return;
    }
    websocketManager.connect(
      listingId,
      data?.state,
      isListingDataCurrent,
      handleMessage,
      refetch,
    );
  }, [
    listingId,
    data?.state,
    isListingDataCurrent,
    handleMessage,
    refetch,
    posthog,
  ]);

  return {
    isListingDataCurrent,
    handleWebsocketMessage: handleMessage,
    listingQueries,
  };
};

export const useAllSellersListings = (queryString?: string) => {
  return useQuery("listings", () =>
    makeSimpleGetRequest<ListingWithIncludes[]>(
      `${ORDERBOOK_URL}/listings/${queryString ? queryString : ""}`,
    ),
  );
};

export const setBuyNowPrice = async (
  listingId: number,
  payload: ListPricePayload,
  posthog: PostHog,
): Promise<EditListing> => {
  const response = await makeSimplePatchRequest<EditListing>(
    `${EDIT_LISTING_URL}/${listingId}/`,
    JSON.stringify(payload),
    [200],
  );
  posthog?.capture("Edited Listing Buy Now Price");
  return response;
};

export const updateMinimumPrice = async (
  listingId: number,
  minimumPrice: number,
  posthog: PostHog,
): Promise<EditListing> => {
  const response = await makeSimplePatchRequest<EditListing>(
    `${EDIT_LISTING_URL}/${listingId}/`,
    JSON.stringify({ minimum_price: minimumPrice }),
    [200],
  );
  posthog?.capture("Edited Listing Minimum Price");
  return response;
};

export const removeBuyNowPrice = async (
  listingId: number,
  posthog: PostHog,
): Promise<EditListing> => {
  const response = await makeSimplePatchRequest<EditListing>(
    `${EDIT_LISTING_URL}/${listingId}/`,
    JSON.stringify({ buy_now_price: null }),
    [200],
  );
  posthog?.capture("Remove Listing Buy Now Price");
  return response;
};

export const createListingFeatureRequest = async (listingId: number) =>
  await makeSimplePostRequest<{ listing: number; state: string }>(
    `${ORDERBOOK_URL}/listing-feature-requests/`,
    JSON.stringify({ listing: listingId }),
    [201],
  );
